export type MenuStyle = "navy" | "light";
export type DialogStyle = "classic" | "light";
export type ConferenceDetail = "table" | "cards";

export const ANTECIPACOES_COLUMN_IDS = [
  "crm",
  "company_name",
  "request_date",
  "payment_date",
  "vacancies_qty",
  "total_gross_value",
  "total_pay_value",
  "total_fee_value",
] as const;
export type AntecipacoesColumnId = (typeof ANTECIPACOES_COLUMN_IDS)[number];

export type Preferences = {
  menuStyle: MenuStyle;
  menuCollapsed: boolean;
  dialogStyle: DialogStyle;
  conferenceDetail: ConferenceDetail;
  antecipacoesColumns: AntecipacoesColumnId[];
};

// Padrão (G1.4): todas as colunas menos o valor bruto e a taxa.
// Congelado: o padrão é compartilhado; sanitizePreferences devolve sempre uma cópia.
export const DEFAULT_ANTECIPACOES_COLUMNS: AntecipacoesColumnId[] = Object.freeze(
  ANTECIPACOES_COLUMN_IDS.filter((id) => id !== "total_gross_value" && id !== "total_fee_value")
) as AntecipacoesColumnId[];

// Padrão A/A/A para quem nunca escolheu; menu começa recolhido no 1º acesso.
export const DEFAULT_PREFERENCES: Preferences = Object.freeze({
  menuStyle: "navy",
  menuCollapsed: true,
  dialogStyle: "classic",
  conferenceDetail: "table",
  antecipacoesColumns: DEFAULT_ANTECIPACOES_COLUMNS,
}) as Preferences;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const oneOf = <T extends string>(v: unknown, options: readonly T[]): v is T =>
  typeof v === "string" && (options as readonly string[]).includes(v);

// Só chaves conhecidas com valor válido; colunas sem ids desconhecidos/repetidos, na ordem canônica.
export function sanitizePartial(raw: unknown): Partial<Preferences> {
  if (!isObject(raw)) return {};
  const out: Partial<Preferences> = {};
  if (oneOf(raw.menuStyle, ["navy", "light"] as const)) out.menuStyle = raw.menuStyle;
  if (typeof raw.menuCollapsed === "boolean") out.menuCollapsed = raw.menuCollapsed;
  if (oneOf(raw.dialogStyle, ["classic", "light"] as const)) out.dialogStyle = raw.dialogStyle;
  if (oneOf(raw.conferenceDetail, ["table", "cards"] as const)) out.conferenceDetail = raw.conferenceDetail;
  if (Array.isArray(raw.antecipacoesColumns)) {
    const wanted = new Set(
      raw.antecipacoesColumns.filter((id): id is AntecipacoesColumnId => oneOf(id, ANTECIPACOES_COLUMN_IDS))
    );
    out.antecipacoesColumns = ANTECIPACOES_COLUMN_IDS.filter((id) => wanted.has(id));
  }
  return out;
}

export function sanitizePreferences(raw: unknown): Preferences {
  const merged = { ...DEFAULT_PREFERENCES, ...sanitizePartial(raw) };
  return { ...merged, antecipacoesColumns: [...merged.antecipacoesColumns] };
}
